import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full h-9 px-3 rounded-sm border border-[#E6E4DD] text-sm outline-none focus:border-forest bg-white";

const Pill = ({ s, slug }) => {
  if (!s) return <span className="text-xs text-forest/40">…</span>;
  const tone = s.connected ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-clay/10 text-clay";
  return (
    <span data-testid={`sa-org-wa-status-${slug}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-semibold ${tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.connected ? "bg-[#2D6A4F]" : "bg-clay"}`} />
      {s.connected ? "Connected" : s.state === "not_configured" ? "Not set up" : "Not connected"}
      <span className="font-normal opacity-70">· {s.scope === "org" ? "own keys" : "global"}</span>
    </span>
  );
};

// Per-organisation Evolution API credentials (falls back to the global ones when empty).
export const OrgWhatsApp = ({ o }) => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ url: "", api_key: "", instance: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/super-admin/organizations/${o.id}/whatsapp`).then((r) => setS(r.data)).catch(() => setS({ state: "error", scope: "global" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [o.id]);
  const start = () => { setF({ url: s?.url || "", api_key: "", instance: s?.instance || "" }); setOpen(true); };
  const save = async (clear = false) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/super-admin/organizations/${o.id}/whatsapp`, clear ? { url: "" } : f, { timeout: 30000 });
      setS(data); setOpen(false);
      toast.success(clear ? `${o.name} now uses the global WhatsApp keys` : data.connected ? `${o.name} WhatsApp connected` : `Saved — status: ${data.state}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <Pill s={s} slug={o.slug} />
      <button data-testid={`sa-org-wa-edit-${o.slug}`} onClick={start} title="WhatsApp settings" className="text-forest/60 hover:text-forest p-1"><MessageCircle className="h-3.5 w-3.5" /></button>
      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent data-testid="sa-org-wa-dialog" className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">WhatsApp settings · {o.name}</DialogTitle></DialogHeader>
            <p className="text-xs text-forest/60">Evolution API credentials used by everyone in this organisation. Leave the URL empty to use the global keys.</p>
            <div className="space-y-3">
              <label className="block"><div className="label-caps mb-1">Evolution API URL</div>
                <input data-testid="sa-org-wa-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://evolution.example.com" className={inputCls} /></label>
              <label className="block"><div className="label-caps mb-1">API key</div>
                <input data-testid="sa-org-wa-key" type="password" value={f.api_key} onChange={(e) => setF({ ...f, api_key: e.target.value })}
                  placeholder={s?.key_set ? "Saved — leave blank to keep" : "Evolution API key"} className={inputCls} /></label>
              <label className="block"><div className="label-caps mb-1">Instance name</div>
                <input data-testid="sa-org-wa-instance" value={f.instance} onChange={(e) => setF({ ...f, instance: e.target.value })} placeholder="e.g. jagathi-main" className={inputCls} /></label>
            </div>
            <div className="text-xs text-forest/60">Current: <Pill s={s} slug={`${o.slug}-dialog`} /> {s?.linked_users ? `· ${s.linked_users} executive number(s) linked` : ""}</div>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {s?.scope === "org" && <button data-testid="sa-org-wa-clear" disabled={busy} onClick={() => save(true)} className="h-9 px-3 rounded-sm border border-[#E6E4DD] text-sm text-clay mr-auto">Use global keys</button>}
              <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-sm border border-[#E6E4DD] text-sm">Cancel</button>
              <button data-testid="sa-org-wa-save" disabled={busy} onClick={() => save(false)} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft disabled:opacity-60">{busy ? "Checking…" : "Save & check"}</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
